import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import FenceDiagram from '../components/FenceDiagram'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeFence, DEFAULT_FENCE } from '../calculators/fence'
import type { FenceInputs, FenceStyle } from '../calculators/fence'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/fence/'
const CATEGORY = 'outdoor-and-landscaping'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  savePrefix: string
  fenceStyles: { v: FenceStyle; l: string }[]
  totalLengthLabel: string
  totalLengthTip: string
  fenceStyleLabel: string
  postSpacingLabel: string
  postSpacingTip: string
  railsLabel: string
  railsTip: string
  railsWord: (n: number) => string
  picketWidthLabel: string
  boardWidthLabel: string
  picketWidthTip: string
  gateSetsLabel: string
  gateSetsTip: string
  gatesSuffix: string
  wasteTip: string
  ftSuffix: string
  linFtSuffix: string
  inSuffix: string
  primaryName: string
  primaryLabel: string
  primaryQuery: string
  secondaryName: string
  secondaryLabel: string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: { strong: string; rest: string }[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Fence Calculator — Posts, Rails, Pickets and Concrete | RenoSheets',
  desc: 'Free fence calculator for picket, privacy and split-rail fences. Enter total length for post count, rails, pickets, concrete bags and gate hardware. No signup.',
  h1: 'Fence Calculator',
  intro: 'Enter total fence length for post count, rails, boards, concrete bags and gate hardware — for any fence style.',
  savePrefix: 'Fence',
  fenceStyles: [
    { v: 'picket', l: 'Picket' },
    { v: 'privacy', l: 'Privacy' },
    { v: 'split-rail', l: 'Split Rail' },
  ],
  totalLengthLabel: 'Total fence length',
  totalLengthTip: 'Measure the full perimeter or run of fence in linear feet. Include all sides and subtract gate openings if desired.',
  fenceStyleLabel: 'Fence style',
  postSpacingLabel: 'Post spacing',
  postSpacingTip: '8 ft is standard for most fence styles. Split-rail fences often use 8–10 ft spacing. Never exceed 10 ft for privacy fences — the rails will sag.',
  railsLabel: 'Rails per span',
  railsTip: '2 rails is standard for 4–5 ft fences. Use 3 rails for 6 ft privacy fences to prevent bowing. Split-rail: 2 for 3-board, 3 for standard.',
  railsWord: (n) => `${n} rails`,
  picketWidthLabel: 'Picket width',
  boardWidthLabel: 'Board width',
  picketWidthTip: 'Standard dog-ear privacy boards are 3.5 in actual (1x4). Pickets are also typically 3.5 in. Wider boards (5.5 in) are sometimes used for contemporary fences.',
  gateSetsLabel: 'Gate sets',
  gateSetsTip: 'Each gate set includes hinges and a latch. Count walk-through gates and driveway gates separately.',
  gatesSuffix: 'gates',
  wasteTip: '10% covers end cuts and damaged boards. Pressure-treated lumber can be knotty; bump to 12–15% for better yield.',
  ftSuffix: 'ft',
  linFtSuffix: 'lin ft',
  inSuffix: 'in',
  primaryName: 'Posts',
  primaryLabel: 'fence posts needed',
  primaryQuery: '4x4 fence post pressure treated 8 ft',
  secondaryName: 'Concrete bags',
  secondaryLabel: '60 lb bags',
  aboutH2: 'What actually affects how much fence material you need',
  aboutP1: 'Post spacing drives almost every other quantity in a fence project, and it varies by style and material. Wood privacy fencing typically uses 8-foot spacing between posts, but taller fences or high-wind areas often need posts closer together — every extra post also adds a hole, a bag of concrete, and extra rail length. Post depth matters just as much as spacing: as a rule of thumb, at least a third of the post length should be below ground, and in cold climates the hole needs to reach below the local frost line or the post can heave over winter. Gate openings need their own math — gate posts are typically set in larger, deeper holes than line posts because they carry more lateral stress from the swinging gate. Board style changes waste too: dog-eared and pointed pickets both use standard board widths but lose a bit of usable length to the cut, so budget slightly more board footage for a picket fence than for a flat-top privacy fence of the same length.',
  howH2: 'How to calculate fence materials',
  howP1: 'Fence material quantities start with post count, then derive rails and boards from post spacing:',
  formula: `posts = ceil(fence length ÷ post spacing) + 1
spans = posts − 1
rails = spans × rail count × ceil(spacing ÷ 8 ft)
boards = ceil(fence length × 12 in ÷ board width) × (1 + waste%)`,
  howP2: 'Concrete: 2 × 60 lb bags per post. Rails are priced as 8 ft treated lumber; privacy boards as 1×4 dog-ear boards. Always add extra boards for future repairs.',
  mistakesH3: 'Common mistakes',
  mistakes: [
    { strong: 'Setting posts too shallow', rest: ' — must go below frost depth to prevent heaving in cold climates.' },
    { strong: 'Installing before calling 811', rest: ' — always request a utility locate before digging post holes.' },
    { strong: 'Not checking property lines', rest: " — a fence on the neighbour's property is a legal problem." },
    { strong: 'Using non-ground-contact lumber', rest: ' for posts — use UC4B or UC4C rated PT for in-ground use.' },
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: linear fence length divided by post spacing plus 1; rails and boards derived from span count. Last updated 2026-05-26. Estimates only — verify quantities before purchasing.',
  faqs: [
    { q: 'How many fence posts do I need for 100 feet of fence?', a: 'At 8 ft post spacing, a 100 ft fence needs 13 posts (100 ÷ 8 = 12.5 sections, plus the final end post = 13 posts). Add one more for each gate opening and always round up — running out of posts mid-project means extra trips.' },
    { q: 'How deep should fence posts be set?', a: 'The standard rule is one-third of the total post length below ground. For a 6 ft fence you need 8 ft posts with at least 2 ft in the ground (deeper in cold climates to get below the frost line). Check your local frost depth — shallow posts heave out of the ground over winter.' },
    { q: 'How many concrete bags do I need per fence post?', a: 'Two 60 lb bags per post hole is standard for 4×4 posts in 10-in diameter holes at 2 ft depth. For 6×6 posts or deeper holes, use 2–3 bags of 80 lb concrete. Dry-set method (pouring dry concrete in the hole and watering from the top) works well for fence posts.' },
    { q: 'How many pickets are in a privacy fence for 100 feet?', a: 'With standard 3.5 in dog-ear boards and no gap (butted tight), a 100 ft privacy fence needs about 343 boards (100 ft × 12 in/ft ÷ 3.5 in). Adding 10% waste gives 378 boards. The calculator handles this automatically based on your picket width.' },
    { q: 'How long does a fence post last in the ground?', a: 'Pressure-treated pine posts rated for ground contact (UC4B or UC4C) typically last 20–40 years. Cedar posts last 15–20 years. Setting the post in concrete actually shortens life slightly because water pools at the concrete-wood interface. Use concrete only at the surface; pack gravel at the bottom for drainage.' },
    { q: 'Do I need a permit to build a fence?', a: 'Many municipalities require a permit for fences over 6 ft tall, for fences near property lines, or in front yards. Some areas have HOA rules too. Always check with your local building department and HOA before installing. A fence in the wrong location can be costly to move.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de clôture — Poteaux, lisses, planches et béton | RenoSheets',
  desc: 'Calculateur de clôture gratuit pour clôtures à planches, d’intimité et de perches refendues. Entrez la longueur totale pour le nombre de poteaux, de lisses, de planches, de sacs de béton et de quincaillerie de barrière. Sans inscription.',
  h1: 'Calculateur de clôture',
  intro: 'Entrez la longueur totale de la clôture pour le nombre de poteaux, de lisses, de planches, de sacs de béton et de quincaillerie de barrière — pour tout style de clôture.',
  savePrefix: 'Clôture',
  fenceStyles: [
    { v: 'picket', l: 'À planches' },
    { v: 'privacy', l: 'Intimité' },
    { v: 'split-rail', l: 'Perches refendues' },
  ],
  totalLengthLabel: 'Longueur totale de la clôture',
  totalLengthTip: 'Mesurez tout le périmètre ou le tracé de la clôture en pieds linéaires. Incluez tous les côtés et soustrayez les ouvertures de barrière au besoin.',
  fenceStyleLabel: 'Style de clôture',
  postSpacingLabel: 'Espacement des poteaux',
  postSpacingTip: '8 pi est standard pour la plupart des styles. Les clôtures de perches refendues utilisent souvent un espacement de 8 à 10 pi. Ne dépassez jamais 10 pi pour les clôtures d’intimité — les lisses s’affaisseront.',
  railsLabel: 'Lisses par travée',
  railsTip: '2 lisses est standard pour les clôtures de 4 à 5 pi. Utilisez 3 lisses pour les clôtures d’intimité de 6 pi afin d’éviter le voilement. Perches refendues : 2 pour 3 perches, 3 pour le standard.',
  railsWord: (n) => `${n} lisses`,
  picketWidthLabel: 'Largeur des planches',
  boardWidthLabel: 'Largeur des planches',
  picketWidthTip: 'Les planches d’intimité à oreilles de chien standard font 3,5 po réels (1x4). Les planches de clôture font aussi habituellement 3,5 po. Des planches plus larges (5,5 po) sont parfois utilisées pour des clôtures contemporaines.',
  gateSetsLabel: 'Ensembles de barrière',
  gateSetsTip: 'Chaque ensemble de barrière comprend des pentures et un loquet. Comptez séparément les barrières piétonnes et les barrières d’entrée.',
  gatesSuffix: 'barrières',
  wasteTip: '10 % couvre les coupes de bout et les planches abîmées. Le bois traité peut être noueux; montez à 12–15 % pour un meilleur rendement.',
  ftSuffix: 'pi',
  linFtSuffix: 'pi lin',
  inSuffix: 'po',
  primaryName: 'Poteaux',
  primaryLabel: 'poteaux de clôture nécessaires',
  primaryQuery: '4x4 fence post pressure treated 8 ft',
  secondaryName: 'Sacs de béton',
  secondaryLabel: 'sacs de 60 lb',
  aboutH2: 'Ce qui influence vraiment la quantité de matériaux de clôture',
  aboutP1: 'L’espacement des poteaux détermine presque toutes les autres quantités d’un projet de clôture, et il varie selon le style et le matériau. Une clôture de confidentialité en bois utilise habituellement un espacement de 8 pieds entre les poteaux, mais les clôtures plus hautes ou les zones très venteuses demandent souvent des poteaux plus rapprochés — chaque poteau supplémentaire ajoute aussi un trou, un sac de béton et de la longueur de lisse. La profondeur des poteaux compte tout autant que l’espacement : en règle générale, au moins un tiers de la longueur du poteau devrait être sous terre, et dans les climats froids, le trou doit descendre sous la ligne de gel locale, sinon le poteau peut soulever pendant l’hiver. Les ouvertures de portail demandent leur propre calcul — les poteaux de portail sont habituellement installés dans des trous plus grands et plus profonds que les poteaux de ligne, car ils subissent plus de tension latérale du portail qui pivote. Le style de planche change aussi la perte : les piquets en oreille de chien et les piquets pointus utilisent tous deux des largeurs de planche standards mais perdent un peu de longueur utile à la coupe, alors prévoyez un peu plus de pieds linéaires de planche pour une clôture à piquets que pour une clôture de confidentialité à sommet plat de la même longueur.',
  howH2: 'Comment calculer les matériaux de clôture',
  howP1: 'Les quantités de matériaux partent du nombre de poteaux, puis on en déduit les lisses et les planches selon l’espacement des poteaux :',
  formula: `poteaux = arrondi.sup(longueur ÷ espacement) + 1
travées = poteaux − 1
lisses = travées × nombre de lisses × arrondi.sup(espacement ÷ 8 pi)
planches = arrondi.sup(longueur × 12 po ÷ largeur planche) × (1 + % perte)`,
  howP2: 'Béton : 2 sacs de 60 lb par poteau. Les lisses sont vendues comme du bois traité de 8 pi; les planches d’intimité comme des planches 1×4 à oreilles de chien. Ajoutez toujours des planches en plus pour les réparations futures.',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    { strong: 'Poser les poteaux trop peu profonds', rest: ' — ils doivent descendre sous la profondeur de gel pour éviter le soulèvement en climat froid.' },
    { strong: 'Creuser avant d’appeler Info-Excavation', rest: ' — demandez toujours une localisation des services publics avant de creuser les trous de poteau.' },
    { strong: 'Ne pas vérifier les limites de propriété', rest: ' — une clôture sur le terrain du voisin est un problème juridique.' },
    { strong: 'Utiliser du bois non conçu pour le contact au sol', rest: ' pour les poteaux — utilisez du bois traité coté UC4B ou UC4C pour l’usage enfoui.' },
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : longueur linéaire de la clôture divisée par l’espacement des poteaux plus 1; lisses et planches déduites du nombre de travées. Dernière mise à jour 2026-05-26. Estimations seulement — vérifiez les quantités avant d’acheter.',
  faqs: [
    { q: 'Combien de poteaux de clôture pour 100 pieds de clôture?', a: 'À un espacement de 8 pi, une clôture de 100 pi demande 13 poteaux (100 ÷ 8 = 12,5 sections, plus le poteau d’extrémité final = 13 poteaux). Ajoutez-en un de plus par ouverture de barrière et arrondissez toujours à la hausse — manquer de poteaux en plein projet signifie des allers-retours.' },
    { q: 'À quelle profondeur poser les poteaux de clôture?', a: 'La règle standard est un tiers de la longueur totale du poteau sous terre. Pour une clôture de 6 pi, il faut des poteaux de 8 pi avec au moins 2 pi dans le sol (plus profond en climat froid pour passer sous la ligne de gel). Vérifiez votre profondeur de gel locale — des poteaux trop peu profonds sont soulevés du sol durant l’hiver.' },
    { q: 'Combien de sacs de béton par poteau de clôture?', a: 'Deux sacs de 60 lb par trou de poteau est standard pour des poteaux 4×4 dans des trous de 10 po de diamètre à 2 pi de profondeur. Pour des poteaux 6×6 ou des trous plus profonds, utilisez 2 à 3 sacs de béton de 80 lb. La méthode à sec (verser le béton sec dans le trou et arroser par le dessus) fonctionne bien pour les poteaux de clôture.' },
    { q: 'Combien de planches dans une clôture d’intimité de 100 pieds?', a: 'Avec des planches à oreilles de chien standard de 3,5 po sans espace (jointées serré), une clôture d’intimité de 100 pi demande environ 343 planches (100 pi × 12 po/pi ÷ 3,5 po). En ajoutant 10 % de perte, on obtient 378 planches. Le calculateur s’en occupe automatiquement selon la largeur de vos planches.' },
    { q: 'Combien de temps dure un poteau de clôture dans le sol?', a: 'Les poteaux de pin traité cotés pour le contact au sol (UC4B ou UC4C) durent généralement de 20 à 40 ans. Les poteaux de cèdre durent de 15 à 20 ans. Sceller le poteau dans le béton raccourcit en fait un peu sa vie parce que l’eau stagne à l’interface béton-bois. Utilisez le béton seulement en surface; tassez du gravier au fond pour le drainage.' },
    { q: 'Ai-je besoin d’un permis pour construire une clôture?', a: 'Plusieurs municipalités exigent un permis pour les clôtures de plus de 6 pi, pour les clôtures près des limites de propriété ou en cour avant. Certains secteurs ont aussi des règles d’association. Vérifiez toujours auprès de votre service du bâtiment avant d’installer. Une clôture au mauvais endroit peut coûter cher à déplacer.' },
  ],
}

export default function FenceCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<FenceInputs>(DEFAULT_FENCE)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeFence(i, locale), [i, locale])
  const set = (patch: Partial<FenceInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'fence',
      name: `${c.savePrefix} — ${i.totalLengthFt} ${c.linFtSuffix}`,
      createdAt: new Date().toISOString(),
      summary: `${r.posts} · ${r.rails} · ${r.pickets > 0 ? `${r.pickets} · ` : ''}${r.concreteBags60lb}`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'fence')

  const assumptions = locale === 'fr'
    ? `${i.totalLengthFt} pi lin · espacement des poteaux de ${i.postSpacingFt} pi · ${i.railCount} lisses · ${i.wastePct} % de perte.`
    : `${i.totalLengthFt} lin ft · ${i.postSpacingFt} ft post spacing · ${i.railCount} rails · ${i.wastePct}% waste.`
  const boardsWord = locale === 'fr' ? 'planches' : 'boards'
  const summaryLine = locale === 'fr'
    ? `${r.posts} poteaux · ${r.rails} lisses · ${r.pickets > 0 ? `${r.pickets} ${boardsWord} · ` : ''}${r.concreteBags60lb} sacs de béton`
    : `${r.posts} posts · ${r.rails} rails · ${r.pickets > 0 ? `${r.pickets} ${boardsWord} · ` : ''}${r.concreteBags60lb} concrete bags`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeFence: posts = ceil(length / spacing) + 1, spans = posts − 1,
  // rails = spans × railCount × ceil(spacing/8) × (1 + waste%),
  // boards = ceil(length × 12 / (width + gap)) × (1 + waste%), concrete = posts × 2.
  const spans = Math.max(0, r.posts - 1)
  const railsPerSpan = Math.ceil(i.postSpacingFt / 8)
  const gapIn = i.fenceStyle === 'privacy' ? 0 : 0.5
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {locale === 'fr'
          ? `Poteaux = arrondi.sup(${i.totalLengthFt} ÷ ${i.postSpacingFt}) + 1`
          : `Posts = ceil(${i.totalLengthFt} ÷ ${i.postSpacingFt}) + 1`}
        {' '}= <strong>{r.posts}</strong>
      </li>
      <li>
        {locale === 'fr' ? `Travées = ${r.posts} − 1` : `Spans = ${r.posts} − 1`}
        {' '}= <strong>{spans}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Lisses = ${spans} × ${i.railCount} × ${railsPerSpan} × (1 + ${i.wastePct}%)`
          : `Rails = ${spans} × ${i.railCount} × ${railsPerSpan} × (1 + ${i.wastePct}%)`}
        {' '}= <strong>{r.rails}</strong>
      </li>
      {i.fenceStyle !== 'split-rail' && (
        <li>
          {locale === 'fr'
            ? `Planches = arrondi.sup(${i.totalLengthFt}×12 ÷ (${i.picketWidthIn}+${gapIn})) × (1 + ${i.wastePct}%)`
            : `Boards = ceil(${i.totalLengthFt}×12 ÷ (${i.picketWidthIn}+${gapIn})) × (1 + ${i.wastePct}%)`}
          {' '}= <strong>{r.pickets} {locale === 'fr' ? 'planches' : 'boards'}</strong>
        </li>
      )}
      <li>
        {locale === 'fr'
          ? `Béton: ${r.posts} poteaux × 2 sacs`
          : `Concrete: ${r.posts} posts × 2 bags`}
        {' '}= <strong>{r.concreteBags60lb} {locale === 'fr' ? 'sacs de 60 lb' : '60 lb bags'}</strong>
      </li>
      {i.gateSets > 0 && (
        <li>
          {locale === 'fr' ? `Barrières` : `Gates`}: <strong>{i.gateSets} {locale === 'fr' ? (i.gateSets === 1 ? 'ensemble' : 'ensembles') : (i.gateSets === 1 ? 'kit' : 'kits')}</strong>
        </li>
      )}
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('fence', 'Fence Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Outdoor & Landscaping', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('fence', 'Fence Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>

          {/* Total length */}
          <div>
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.totalLengthLabel}
              <InfoTip text={c.totalLengthTip} />
            </span>
            <SmartField label="" imperialValue={i.totalLengthFt} onChangeImperial={(n) => set({ totalLengthFt: n })} imperialUnit="ft" />
          </div>

          {/* Fence style */}
          <div className="mt-4">
            <Segmented
              label={c.fenceStyleLabel}
              value={i.fenceStyle}
              onChange={(v) => set({ fenceStyle: v as FenceStyle })}
              options={c.fenceStyles}
            />
          </div>

          {/* Post spacing */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.postSpacingLabel}
              <InfoTip text={c.postSpacingTip} />
            </span>
            <SmartField label="" imperialValue={i.postSpacingFt} onChangeImperial={(n) => set({ postSpacingFt: n })} imperialUnit="ft" />
          </div>

          {/* Rail count */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.railsLabel}
              <InfoTip text={c.railsTip} />
            </span>
            <div className="flex gap-3">
              {[2, 3].map((n) => (
                <label key={n} className="flex items-center gap-1.5 text-sm text-text-body">
                  <input type="radio" checked={i.railCount === n} onChange={() => set({ railCount: n })} />
                  {c.railsWord(n)}
                </label>
              ))}
            </div>
          </div>

          {/* Picket width */}
          {i.fenceStyle !== 'split-rail' && (
            <div className="mt-4">
              <span className="mb-1 flex items-center text-sm font-medium text-text-body">
                {i.fenceStyle === 'picket' ? c.picketWidthLabel : c.boardWidthLabel}
                <InfoTip text={c.picketWidthTip} />
              </span>
              <SmartField label="" imperialValue={i.picketWidthIn} onChangeImperial={(n) => set({ picketWidthIn: n })} imperialUnit="in" />
            </div>
          )}

          {/* Gates */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.gateSetsLabel}
              <InfoTip text={c.gateSetsTip} />
            </span>
            <Field label="" value={i.gateSets} onChange={(n) => set({ gateSets: Math.round(n) })} suffix={c.gatesSuffix} />
          </div>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={10} max={15} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">{summaryLine}</p>
          </div>

          {/* Side-elevation of one section — shows the style, rail count, post
              spacing and board width visually as the user changes them. */}
          <div className="mt-5 flex flex-col items-center rounded-lg bg-surface-2 p-3">
            <p className="mb-1 text-sm font-semibold text-text">{c.fenceStyles.find((o) => o.v === i.fenceStyle)?.l}</p>
            <FenceDiagram style={i.fenceStyle} railCount={i.railCount} postSpacingFt={i.postSpacingFt} picketWidthIn={i.picketWidthIn} locale={locale} />
            <p className="mt-2 text-xs text-text-subtle">{locale === 'fr' ? 'Une section de clôture — poteaux, lisses et planches à l’échelle.' : 'One fence section — posts, rails and boards to scale.'}</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: c.primaryLabel, qty: r.posts, unit: 'post', query: c.primaryQuery }}
            secondary={{ name: c.secondaryName, label: c.secondaryLabel, qty: r.concreteBags60lb, unit: 'bag', query: 'quikrete 60 lb concrete fence post' }}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}><strong>{m.strong}</strong>{m.rest}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
